// src/context/auth-context.tsx
import React from "react";
import { User } from "../types/user";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { useLoginMutation, useRegisterMutation, authApi } from "./auth-api";

import { client } from "./api-client";
import { useAsync } from "./hooks"; // your custom useAsync hook
import { baseApi } from "../store/api/baseApi";
import { clearAuth, setAuth } from "../store/auth-slice";
import { setAIMode } from "../store/ai-slice";

export interface AuthContextProps {
  data: User | null;
  login: (credentials: { username: string; password: string }) => Promise<User>;
  register: (payload: { password: string; token: string }) => Promise<User>;
  logout: () => Promise<void>;
}

const AuthContext = React.createContext<AuthContextProps | undefined>(
  undefined,
);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [loginMutation] = useLoginMutation();
  const [registerMutation] = useRegisterMutation();
  const dispatch = useDispatch();
  const history = useHistory();
  // useAsync handles loading / error / status
  const { data, setData, run, isLoading, isError, error, isIdle } = useAsync({
    initialState: { data: null },
  });

  // Initialize from sessionStorage
  React.useEffect(() => {
    localStorage.removeItem("ps-user");
    const stored = sessionStorage.getItem("ps-user");
    if (stored) {
      const parsed = JSON.parse(stored);

      setData(parsed);

      dispatch(
        setAuth({
          user: parsed,
          token: parsed.token,
        }),
      );
    }
  }, [setData, dispatch]);

  // Login function
  const login = React.useCallback(
    async (credentials: { username: string; password: string }) => {
      const response = await loginMutation(credentials).unwrap();
      dispatch(setAIMode(false));
      // localStorage.removeItem("PS-AIMode");
      // extract inner user
      const user = response.user;
      if (user) {
        setData(user);
        sessionStorage.setItem("ps-user", JSON.stringify(user));
        dispatch(
          setAuth({
            user: user,
            token: user.token,
          }),
        );
      }
      return user!;
    },
    [loginMutation, run, setData, dispatch],
  );

  // Register function
  const register = React.useCallback(
    async (payload: { password: string; token: string }) => {
      const promise = registerMutation(payload).unwrap();
      const response = await run(promise);
      const user = response.user;
      if (user) {
        setData(user);
        sessionStorage.setItem("ps-user", JSON.stringify(user));
        dispatch(
          setAuth({
            user: user,
            token: user.token,
          }),
        );
      }
      return user!;
    },
    [registerMutation, run, setData, dispatch],
  );

  // Logout function

  const logout = React.useCallback(async () => {
    setData(null);
    dispatch(setAIMode(false));
    dispatch(clearAuth());
    dispatch(baseApi.util.resetApiState());
    dispatch(authApi.util.resetApiState());
    sessionStorage.removeItem("ps-user");
    localStorage.removeItem("ps-user");
    localStorage.removeItem("PS-Reminder");
    localStorage.removeItem("PS-IsSeeOwnReview");
    localStorage.removeItem("PS-IsSameOrganization");
    localStorage.removeItem("PS-AIMode");

    history?.push("/");
  }, [dispatch]);

  const value = React.useMemo(
    () => ({ data, login, register, logout }),
    [data, login, register, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// === Hook to access auth context ===
export function useAuth(): AuthContextProps {
  const context = React.useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}

// === Hook to access client with token automatically ===
